const integerFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const longDateFormat = new Intl.DateTimeFormat("en-US", {
	weekday: "long",
	year: "numeric",
	month: "long",
	day: "numeric",
	timeZone: "UTC",
});

/**
 * `1010000` → `"1,010,000"`, and a figure that rounds to zero from below → `"0"`, never `"-0"`. Done by hand rather
 * than with `signDisplay: "negative"`, which browsers before Firefox 116 and Safari 15.4 refuse, failing every page.
 */
export function formatInteger(value: number): string {
	const text = integerFormat.format(value);
	return text === "-0" ? "0" : text;
}

/** `1010000` → `"USD 1,010,000"` (the console shows currency codes, not symbols). */
export function formatUsd(amount: number): string {
	return `USD ${formatInteger(amount)}`;
}

/** `(14900.5, "EUR")` → `"EUR 14,901"`: any currency, by its code as `formatUsd` shows dollars, to the whole unit. */
export function formatMoney(amount: number, currency: string): string {
	return `${currency} ${formatInteger(amount)}`;
}

const centsFormat = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * `("1010000.00", "USD")` → `"USD 1,010,000.00"`, `("-12.5", "EUR")` → `"EUR -12.50"`: an amount as a bank states it, a
 * decimal string, to the cent — where `formatMoney` rounds to the whole unit.
 */
export function formatAmount(amount: string, currency: string): string {
	return `${currency} ${centsFormat.format(Number(amount))}`;
}

/** `"2026-09-13"` → `"Sunday, September 13, 2026"`. Formatted in UTC so no time zone moves the calendar day. */
export function formatLongDate(isoDate: string): string {
	return longDateFormat.format(new Date(`${isoDate}T00:00:00Z`));
}

/** `"2021-04-01"` → `"Apr 1, 2021"`. Formatted in UTC so no time zone moves the calendar day. */
export function formatShortDate(isoDate: string): string {
	return shortDateWithYearFormat.format(new Date(`${isoDate}T00:00:00Z`));
}

/** `"2026-09-13T16:42:00Z"` in `"Europe/Ljubljana"` → `"Sep 13, 2026, 18:42"` (24-hour clock). */
export function formatDateTime(isoTimestamp: string, timeZone: string): string {
	return new Intl.DateTimeFormat("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
		timeZone,
	}).format(new Date(isoTimestamp));
}

/** `"2026-09-13T16:42:00Z"` in `"Europe/Ljubljana"` → `"18:42"`: the time of day alone, on a 24-hour clock. */
export function formatTime(isoTimestamp: string, timeZone: string): string {
	return new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).format(
		new Date(isoTimestamp),
	);
}

/**
 * `"2026-09-13T23:30:00Z"` in `"Europe/Ljubljana"` → `"Monday, September 14, 2026"`: the day a timestamp falls on in
 * that time zone, written as `formatLongDate` writes a date.
 */
export function formatDay(isoTimestamp: string, timeZone: string): string {
	return new Intl.DateTimeFormat("en-US", {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
		timeZone,
	}).format(new Date(isoTimestamp));
}

const shortDateFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const shortDateWithYearFormat = new Intl.DateTimeFormat("en-US", {
	month: "short",
	day: "numeric",
	year: "numeric",
	timeZone: "UTC",
});

/** `"2026-09-25"` → `"Sep 25"`: a day this year needs no more. Formatted in UTC, so no time zone moves the day. */
export function formatMonthDay(isoDate: string): string {
	return shortDateFormat.format(new Date(`${isoDate}T00:00:00Z`));
}

const rateFormat = new Intl.NumberFormat("en-US", { maximumSignificantDigits: 5 });

/** `1.1403 / 0.86045` → `"1.3252"`: an exchange rate to five significant figures, as the ECB states its own. */
export function formatRate(rate: number): string {
	return rateFormat.format(rate);
}

/**
 * How long ago a timestamp was, the way the activity feed says it: `"just now"`, `"5 min ago"`, `"2h ago"`,
 * `"Yesterday"`, `"3d ago"`, and past a week a short date, `"Sep 8"`, with the year when it is not this one. A time in
 * the future (a clock ahead of ours) is "just now". `now` is for tests.
 */
export function formatRelativeTime(isoTimestamp: string, now: Date = new Date()): string {
	const minute = 60_000;
	const hour = 60 * minute;
	const day = 24 * hour;
	const elapsed = now.getTime() - Date.parse(isoTimestamp);
	if (elapsed < minute) {
		return "just now";
	}
	if (elapsed < hour) {
		return `${Math.floor(elapsed / minute)} min ago`;
	}
	if (elapsed < day) {
		return `${Math.floor(elapsed / hour)}h ago`;
	}
	if (elapsed < 2 * day) {
		return "Yesterday";
	}
	if (elapsed < 7 * day) {
		return `${Math.floor(elapsed / day)}d ago`;
	}
	const date = new Date(isoTimestamp);
	return (date.getUTCFullYear() === now.getUTCFullYear() ? shortDateFormat : shortDateWithYearFormat).format(date);
}

/** Whether dates can be formatted in `timeZone`, an IANA name such as `"Europe/Ljubljana"` or `"UTC"`. */
export function isTimeZone(timeZone: string): boolean {
	try {
		new Intl.DateTimeFormat("en-US", { timeZone });
		return true;
	} catch {
		return false;
	}
}

/** `"Žan K."` → `"ŽK"` */
export function initialsOf(name: string): string {
	return name
		.split(" ")
		.map(word => word.charAt(0))
		.join("");
}
